import { useState } from 'react';
import {
	FormControl,
	FormLabel,
	Grid,
	Icon,
	ListSubheader,
	MenuItem,
	Select,
	TextField,
	Typography
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';

import { tryParseDate } from 'Lib/muiComponents/form/DatePickerField';

const DATE_FORMAT = 'MM/DD/YYYY';

export default function DateRange(props) {
	const [dateRange, setDateRange] = useState('');
	const [open, setOpen] = useState(false);

	const handleFromDateBlur = event => {
		props.onChange(
			props.minName,
			tryParseDate(event.target.value)?.format(DATE_FORMAT)
		);
	};

	const handleToDateBlur = event => {
		props.onChange(
			props.maxName,
			tryParseDate(event.target.value)?.format(DATE_FORMAT)
		);
	};

	const handleFromDateChange = fromDate => {
		props.onChange(
			props.minName,
			tryParseDate(fromDate)?.format(DATE_FORMAT)
		);
	};

	const handleToDateChange = toDate => {
		props.onChange(
			props.maxName,
			tryParseDate(toDate)?.format(DATE_FORMAT)
		);
	};

	const onKeyDown = (event, field) => {
		const keyOperations = {
			ArrowUp: date => date.add(1, 'days'),
			ArrowDown: date => date.subtract(1, 'days'),
			t: () => moment(),
			m: date => date.add(1, 'months'),
			l: date => date.subtract(1, 'months'),
			y: date => date.add(1, 'years'),
			p: date => date.subtract(1, 'years')
		};

		const operation = keyOperations[event.key];

		if (operation) {
			const date = moment(event.target.value, DATE_FORMAT);
			const value = operation(date).format(DATE_FORMAT);

			if (props.onChange) {
				props.onChange(
					field === 'min' ? props.minName : props.maxName,
					value
				);
			}
		}
	};

	const quickFilters = [
		{ value: 'today', onSelected: () => calcToday() },
		{ value: 'last7Days', onSelected: () => calcLastXDays(7) },
		{ value: 'last30Days', onSelected: () => calcLastXDays(30) },
		{ value: 'last60Days', onSelected: () => calcLastXDays(60) },
		{ value: 'last90Days', onSelected: () => calcLastXDays(90) },
		{ value: 'next7Days', onSelected: () => calcNextXDays(7) },
		{ value: 'next30Days', onSelected: () => calcNextXDays(30) },
		{ value: 'next60Days', onSelected: () => calcNextXDays(60) },
		{ value: 'next90Days', onSelected: () => calcNextXDays(90) },
		{ value: 'weekToDate', onSelected: () => calcToDate('week') },
		{ value: 'monthToDate', onSelected: () => calcToDate('month') },
		{ value: 'yearToDate', onSelected: () => calcToDate('year') }
	];

	const calcToday = () => {
		props.onChange(props.minName, moment().format(DATE_FORMAT));
		props.onChange(props.maxName, moment().format(DATE_FORMAT));
	};

	const calcLastXDays = days => {
		props.onChange(
			props.minName,
			moment().subtract(days, 'days').format(DATE_FORMAT)
		);
		props.onChange(props.maxName, moment().format(DATE_FORMAT));
	};

	const calcNextXDays = days => {
		props.onChange(props.minName, moment().format(DATE_FORMAT));
		props.onChange(
			props.maxName,
			moment().add(days, 'days').format(DATE_FORMAT)
		);
	};

	const calcToDate = type => {
		props.onChange(
			props.minName,
			moment().startOf(type).format(DATE_FORMAT)
		);
		props.onChange(props.maxName, moment().format(DATE_FORMAT));
	};

	const handleSelectFieldChange = event => {
		setDateRange(event.target.value);

		quickFilters.find(option => {
			if (option.value === event.target.value) option.onSelected();
		});
	};

	const DropdownIcon = () => (
		<Icon sx={{ mr: '10px' }} onClick={() => setOpen(!open)}>
			{open ? 'expand_less' : 'expand_more'}
		</Icon>
	);

	return (
		<FormControl fullWidth>
			<FormLabel variant="list-filter">{props.label}</FormLabel>
			<LocalizationProvider dateAdapter={AdapterMoment}>
				<Grid container spacing={2}>
					<Grid item container xs={12} spacing={4}>
						<Grid item xs={6}>
							<DatePicker
								label="From"
								value={props.min ? moment(props.min) : null}
								onChange={handleFromDateChange}
								components={{
									OpenPickerIcon: () => (
										<Icon>calendar_month</Icon>
									)
								}}
								renderInput={params => (
									<TextField
										{...params}
										onKeyDown={event =>
											onKeyDown(event, 'min')
										}
										margin={props.margin}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={6}>
							<DatePicker
								label="To"
								value={props.max ? moment(props.max) : null}
								onChange={handleToDateChange}
								components={{
									OpenPickerIcon: () => (
										<Icon>calendar_month</Icon>
									)
								}}
								renderInput={params => (
									<TextField
										{...params}
										onKeyDown={event =>
											onKeyDown(event, 'max')
										}
										margin={props.margin}
									/>
								)}
							/>
						</Grid>
					</Grid>
					<Grid item xs={12}>
						<Select
							value={dateRange}
							displayEmpty
							id="date-range-select"
							renderValue={
								dateRange !== ''
									? undefined
									: () => (
											<Typography
												style={{ color: '#A2A2A2' }}>
												Select date range
											</Typography>
										)
							}
							onChange={handleSelectFieldChange}
							IconComponent={DropdownIcon}
							open={open}
							onOpen={() => setOpen(true)}
							onClose={() => setOpen(false)}
							sx={{ width: '100%' }}>
							<MenuItem value={'today'}>Today</MenuItem>
							<ListSubheader>Last</ListSubheader>
							<MenuItem value={'last7Days'}>Last 7 Days</MenuItem>
							<MenuItem value={'last30Days'}>
								Last 30 Days
							</MenuItem>
							<MenuItem value={'last60Days'}>
								Last 60 Days
							</MenuItem>
							<MenuItem value={'last90Days'}>
								Last 90 Days
							</MenuItem>
							<ListSubheader>Next</ListSubheader>
							<MenuItem value={'next7Days'}>Next 7 Days</MenuItem>
							<MenuItem value={'next30Days'}>
								Next 30 Days
							</MenuItem>
							<ListSubheader>Other</ListSubheader>
							<MenuItem value={'weekToDate'}>
								Week to Date
							</MenuItem>
							<MenuItem value={'monthToDate'}>
								Month to Date
							</MenuItem>
							<MenuItem value={'yearToDate'}>
								Year to Date
							</MenuItem>
						</Select>
					</Grid>
				</Grid>
			</LocalizationProvider>
		</FormControl>
	);
}
